import { create } from 'zustand';
import axios from 'axios';
import { getSlugFromLocation } from '../lib/tenantPath';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Restore auth header from localStorage on app boot (before any component mounts)
const _existingToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
if (_existingToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_existingToken}`;
}

// Path-based multi-tenancy — attach the tenant slug from `/t/<slug>/...` (if
// present) to EVERY outgoing request. Backend uses this header when the JWT
// hasn't been issued yet (login, signup) and as a hardening cross-check
// otherwise. Interceptor runs on every request so slug changes on navigation
// are picked up automatically.
axios.interceptors.request.use((config) => {
  const slug = getSlugFromLocation();
  if (slug) {
    config.headers = config.headers || {};
    config.headers['X-Tenant-Slug'] = slug;
  }
  return config;
});

const useAuthStore = create((set) => ({
  user: null,
  token: _existingToken,
  isAuthenticated: !!_existingToken,
  
  login: async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, null, {
        params: { email, password, remember_me: rememberMe }
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      set({ user, token: access_token, isAuthenticated: true });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  },

  /** Store an already-issued token+user (used by admin console). */
  setAuth: (accessToken, user) => {
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    set({ user, token: accessToken, isAuthenticated: true });
  },

  /**
   * Super-admin only: swap the current admin session for an impersonation
   * session. Backend mints a fresh JWT scoped to the tenant's President; we
   * stash the admin's original token in sessionStorage so `exitImpersonation`
   * can restore it without a re-login. The impersonation JWT carries an
   * `impersonated_by` claim which every audit log downstream inherits.
   *
   * Returns { subdomain } on success so caller can navigate.
   */
  startImpersonation: async (subdomain) => {
    // Snapshot the admin session BEFORE we clobber it.
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      sessionStorage.setItem('vooxy_impersonation_return_token', currentToken);
    }
    try {
      const r = await axios.post(`${API}/admin/tenants/${subdomain}/impersonate`);
      const { access_token, user, tenant } = r.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      set({ user, token: access_token, isAuthenticated: true });
      return { success: true, subdomain: tenant.subdomain };
    } catch (e) {
      // Rollback the snapshot so admin isn't logged out on failure
      sessionStorage.removeItem('vooxy_impersonation_return_token');
      return { success: false, error: e.response?.data?.detail || 'Impersonation failed' };
    }
  },

  /**
   * Restore the admin session that was stashed before impersonation started.
   * If no snapshot exists (page reloaded during impersonation, sessionStorage
   * wiped), we fall back to a hard logout — safer than leaving them stranded.
   */
  exitImpersonation: async () => {
    const returnToken = sessionStorage.getItem('vooxy_impersonation_return_token');
    sessionStorage.removeItem('vooxy_impersonation_return_token');
    if (!returnToken) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      set({ user: null, token: null, isAuthenticated: false });
      return { success: false, needsRelogin: true };
    }
    localStorage.setItem('token', returnToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${returnToken}`;
    try {
      const me = await axios.get(`${API}/auth/me`);
      set({ user: me.data, token: returnToken, isAuthenticated: true });
      return { success: true };
    } catch (e) {
      // Return token expired while impersonating — force re-login.
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      set({ user: null, token: null, isAuthenticated: false });
      return { success: false, needsRelogin: true };
    }
  },
  
  changePassword: async (currentPassword, newPassword) => {
    try {
      const r = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword, new_password: newPassword,
      });
      localStorage.setItem('token', r.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.access_token}`;
      set({ user: r.data.user, token: r.data.access_token, isAuthenticated: true });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Could not change password' };
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    set({ user: null, token: null, isAuthenticated: false });
  },
  
  fetchUser: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API}/auth/me`);
      set({ user: response.data, isAuthenticated: true });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false });
    }
  }
}));

export default useAuthStore;
